import { Button, Empty, Form, Input, InputNumber, Space, Switch, Typography } from 'antd';
import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FORM_CONSTRAINTS, fieldRequiresOptions } from '../constants';
import type { FormFieldModel } from '../types';
import { slugifyKey } from '../utils/fields';
import { supportsMaxLength } from './fieldMeta';

const { Text } = Typography;

interface FieldPropertiesPanelProps {
  field: FormFieldModel | null;
  onChange: (patch: Partial<FormFieldModel>) => void;
  disabled?: boolean;
}

/** Sağ panel — seçili alanın özellikleri (key/label/required/maxLength/options). */
export function FieldPropertiesPanel({ field, onChange, disabled }: FieldPropertiesPanelProps) {
  const { t } = useTranslation('forms');

  if (!field) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('builder.selectFieldHint')}
        style={{ marginTop: 24 }}
      />
    );
  }

  // Etiket değişince key boşsa otomatik türet (kullanıcı özelleştirmişse dokunma).
  const handleLabelChange = (label: string) => {
    const patch: Partial<FormFieldModel> = { label };
    if (field.key.trim().length === 0) patch.key = slugifyKey(label);
    onChange(patch);
  };

  const updateOption = (index: number, value: string) => {
    const options = field.options.map((o, i) => (i === index ? value : o));
    onChange({ options });
  };
  const addOption = () => onChange({ options: [...field.options, ''] });
  const removeOption = (index: number) =>
    onChange({ options: field.options.filter((_, i) => i !== index) });

  return (
    <div>
      <Text strong>{t('builder.properties')}</Text>
      <Form layout="vertical" style={{ marginTop: 12 }} disabled={disabled}>
        <Form.Item label={t('builder.field.label')} required style={{ marginBottom: 12 }}>
          <Input
            value={field.label}
            maxLength={FORM_CONSTRAINTS.maxLabelLength}
            onChange={(e) => handleLabelChange(e.target.value)}
            data-testid="field-prop-label"
          />
        </Form.Item>

        <Form.Item label={t('builder.field.key')} required help={t('builder.field.keyHint')} style={{ marginBottom: 12 }}>
          <Input
            value={field.key}
            maxLength={FORM_CONSTRAINTS.maxKeyLength}
            onChange={(e) => onChange({ key: e.target.value })}
            data-testid="field-prop-key"
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 12 }}>
          <Space>
            <Switch
              checked={field.required}
              onChange={(required) => onChange({ required })}
              data-testid="field-prop-required"
            />
            <Text>{t('builder.field.required')}</Text>
          </Space>
        </Form.Item>

        {supportsMaxLength(field.type) ? (
          <Form.Item label={t('builder.field.maxLength')} style={{ marginBottom: 12 }}>
            <InputNumber
              min={1}
              value={field.maxLength ?? undefined}
              onChange={(v) => onChange({ maxLength: typeof v === 'number' ? v : null })}
              style={{ width: '100%' }}
              data-testid="field-prop-maxlength"
            />
          </Form.Item>
        ) : null}

        {fieldRequiresOptions(field.type) ? (
          <Form.Item label={t('builder.field.options')} required style={{ marginBottom: 12 }}>
            <Space direction="vertical" style={{ width: '100%' }} size={6}>
              {field.options.map((opt, index) => (
                <Space.Compact key={index} style={{ width: '100%' }}>
                  <Input
                    value={opt}
                    placeholder={t('builder.field.optionPlaceholder')}
                    onChange={(e) => updateOption(index, e.target.value)}
                    data-testid="field-prop-option"
                  />
                  <Button
                    danger
                    icon={<Trash2 size={14} />}
                    aria-label={t('builder.field.remove')}
                    onClick={() => removeOption(index)}
                  />
                </Space.Compact>
              ))}
              <Button
                type="dashed"
                icon={<Plus size={14} />}
                onClick={addOption}
                disabled={field.options.length >= FORM_CONSTRAINTS.maxOptionsPerField}
                data-testid="field-prop-add-option"
              >
                {t('builder.field.addOption')}
              </Button>
            </Space>
          </Form.Item>
        ) : null}
      </Form>
    </div>
  );
}
